import { useEffect, useRef, useState, type ChangeEvent } from "react"

import IconMinus from "@/components/icons/minus"
import IconPlus from "@/components/icons/plus"
import { IconButton } from "../ui/icon-button"
import { TextInput } from "../ui/text-input"

interface QuestionNumberInputProps {
  value: number | undefined
  onChange: (value: number) => void
  disabled?: boolean
  min?: number
  max?: number
  placeholder?: string
}

export function QuestionNumberInput({
  value,
  onChange,
  disabled,
  min,
  max,
  placeholder,
}: QuestionNumberInputProps) {
  const [localValue, setLocalValue] = useState(
    value === undefined ? "" : String(value)
  )
  const isFocusedRef = useRef(false)

  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(value === undefined ? "" : String(value))
    }
  }, [value])

  const lower = min ?? -Infinity
  const upper = max ?? Infinity
  const clamp = (n: number) => Math.max(lower, Math.min(upper, n))

  const handleStep = (delta: number) => {
    // First tap on either button seeds the value instead of stepping from nothing.
    const next = value === undefined ? clamp(min ?? 1) : clamp(value + delta)
    setLocalValue(String(next))
    onChange(next)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setLocalValue(raw)
    const parsed = Number(raw)
    if (!Number.isNaN(parsed) && raw !== "") {
      onChange(parsed)
    }
  }

  const handleFocus = () => {
    isFocusedRef.current = true
  }

  const handleBlur = () => {
    isFocusedRef.current = false
    const parsed = Number(localValue)
    if (Number.isNaN(parsed) || localValue === "") {
      setLocalValue("")
      return
    }

    const clamped = clamp(parsed)
    if (clamped !== parsed) {
      onChange(clamped)
    }
    setLocalValue(String(clamped))
  }

  return (
    <div className="flex items-center gap-2">
      <IconButton
        variant="outline"
        size="sm"
        icon={IconMinus}
        onClick={() => handleStep(-1)}
        disabled={disabled || (value !== undefined && value <= lower)}
        label="decrease value"
      />
      <TextInput
        size="sm"
        inputMode="numeric"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        className="w-16 text-center"
      />
      <IconButton
        variant="outline"
        size="sm"
        icon={IconPlus}
        onClick={() => handleStep(1)}
        disabled={disabled || (value !== undefined && value >= upper)}
        label="increase value"
      />
    </div>
  )
}
